import React, { type ReactNode } from 'react';
import { motion } from 'framer-motion';
import { easingCurves } from '../../hooks/useAnimationConfig';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import {
  type AnimationConfig,
  type AnimationVariantName,
  animationVariants
} from '../../types/animation';

interface ScrollSectionProps {
  id: string;
  className?: string;
  children: ReactNode;
  animationVariant?: AnimationVariantName;
  animationConfig?: Partial<AnimationConfig>;
  disableAnimation?: boolean;
}

// Sections reveal once, as soon as their top edge nears the viewport, with a short
// fade. No scroll pausing and no minimum-visible-area requirement: tall sections on
// phones could otherwise take a long time (or never) to reach a large visibility ratio.
const ScrollSection: React.FC<ScrollSectionProps> = ({
  id,
  className = '',
  children,
  animationVariant = 'fadeInUp',
  animationConfig = {},
  disableAnimation = false
}) => {
  const prefersReducedMotion = useReducedMotion();

  if (disableAnimation || prefersReducedMotion) {
    return (
      <section id={id} className={`scroll-mt-16 sm:scroll-mt-20 ${className}`}>
        {children}
      </section>
    );
  }

  const variants = animationVariants[animationVariant];
  const hidden = 'y' in variants.hidden ? { ...variants.hidden, y: Math.sign(variants.hidden.y) * 16 } : variants.hidden;

  return (
    <motion.section
      id={id}
      className={`scroll-mt-16 sm:scroll-mt-20 ${className}`}
      initial={hidden}
      whileInView={variants.visible}
      viewport={{ once: true, amount: 'some', margin: '0px 0px 20% 0px' }}
      transition={{
        duration: 0.35,
        ease: easingCurves[animationConfig.easing === 'easeOutBack' ? 'easeOut' : animationConfig.easing ?? 'easeOut']
      }}
    >
      {children}
    </motion.section>
  );
};

export default ScrollSection;
